import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePost } from "../../hooks/usePost";
import styled from "styled-components";
import { useToast } from "../../store/ToastProvider";
import { SideMenu } from "../../components/common/SideMenu";
import { NavBar } from "../../components/common/NavBar";
import { PortfolioComposition } from "./PortfolioComposition";
import { PortfolioEmissionsPie } from "./PortfolioEmissionsPie";
import {
  RowContainer,
  Card,
  HorizontalLine,
  ElementContainer,
  BackgroundContainer,
  NavBarContainer,
} from "../../components/containers/styledComponents";
import {
  CompassMainHeader,
  CompassParagraph,
} from "../../components/common/styledComponents";
import { Column as BaseColumn } from "../../components/containers/styledComponents";
import { CustomDropdown } from "../../components/visuals/PageFilter/PageFilter";
import { DecarbonisationPathwayCard } from "./DecarbonisationPathwayCard";
import { ImplicitTemperature } from "./ImplicitTemperature";
import { FossilFuelExposure } from "./FossilFuelExposure";
import { ActionTracker } from "./ActionTracker/ActionTracker";
import { ControlPanelGraphData } from "../../models/controlPanelDataModel";
import { TopContributors } from "./TopContributors";
import { LatestNews } from "./LatestNews";
import { Status } from "../../enums/Status";
import { ToastType } from "../../types/toastTypes";
import { Filter } from "../../types/PageFilterTypes";
import {
  createFundFilters,
  DEFAULT_SCOPE_FILTER,
  DEFAULT_FUND_FILTER,
  SCOPE_FILTERS,
  updateFilter,
} from "../../utils/PageFilterUtils";
import { GetControlPanelDataParams as ControlPanelFilters } from "../../types/controlPanelTypes";

import { initialFilters } from "./defaultData";

import { ROUTES } from "../../routes/routeNames";
import { greyText } from "../../styles";
import { MESSAGES } from "../../messages";

const Column = styled(BaseColumn)<{
  show?: boolean;
  bg?: string;
  overflow?: boolean;
}>`
  transition: 0.15s ease;
  min-height: 100vh;
  overflow: hidden;
  min-width: ${({ show }): string => (show ? "300px" : "60px")};
  background-color: ${({ bg }): string => bg || "white"};
  justify-content: flex-start;
  align-items: flex-start;
  overflow-y: ${({ overflow }): string => (overflow ? "auto" : "hidden")};
`;

const MiniChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 50%;
  height: 140px;
`;

const BOTTOM_ROW_HEIGHT = "500px";

const initialState: ControlPanelGraphData = {
  security_types_ratios: [],
  ghg_emissions_by_portfolio: [],
  carbon_pathway_by_portfolio: [],
};

export const ControlPanel = (): JSX.Element => {
  const initialFiltersDeepCopy = JSON.parse(JSON.stringify(initialFilters));

  const { addToast } = useToast();
  const navigate = useNavigate();
  const {
    data,
    error,
    isLoading,
    sendData: getControlPanelData,
  } = usePost<ControlPanelGraphData, ControlPanelFilters>();
  const [controlPanelData, setControlPanelData] =
    useState<ControlPanelGraphData>(initialState);
  const [show, setShow] = useState(false);
  const [fundOptions, setFundOptions] = useState<Filter[]>([]);
  const [controlPanelFilters, setControlPanelFilters] =
    useState<ControlPanelFilters>(initialFiltersDeepCopy);

  useEffect(() => {
    fetchData();
  }, [controlPanelFilters]);

  useEffect(() => {
    if (data) {
      handleSuccessfulDataFetch(data);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      handleFailedDataFetch(
        error.error_user_message ||
          MESSAGES.DEFAULT_GET_CONTROL_PANEL_DATA_ERROR_MESSAGE,
        Status.ERROR,
      );
    }
  }, [error]);

  const shouldSetFundOptions = (): boolean => {
    return fundOptions.length === 0;
  };

  const fetchData = async (): Promise<void> => {
    const appliedFilters = shouldSetFundOptions()
      ? initialFilters
      : controlPanelFilters;

    await getControlPanelData("GET_CONTROL_PANEL_DATA", appliedFilters);
  };

  const handleSuccessfulDataFetch = (data: ControlPanelGraphData): void => {
    // We want to have all the funds in the dropdown. the initial request will have all the funds. We do not want to update the funds if the data as further requests could contain less funds
    shouldSetFundOptions() &&
      setFundOptions(createFundFilters(data.ghg_emissions_by_portfolio));
    setControlPanelData(data);
  };

  const handleFailedDataFetch = (message: string, status: ToastType): void => {
    addToast(message, status);
    navigate(ROUTES.LOGIN);
  };

  const handleFilterChange = (keys: string[], option: Filter): void => {
    const updatedFilters = updateFilter<ControlPanelFilters>(
      controlPanelFilters,
      keys,
      option.key,
    );
    setControlPanelFilters(updatedFilters);
  };

  const handleMultiFilterChange = (keys: string[], options: Filter[]): void => {
    const updatedFilters = updateFilter<ControlPanelFilters>(
      controlPanelFilters,
      keys,
      options.map((option) => option.key),
    );
    setControlPanelFilters(updatedFilters);
  };

  const fundFilterChangeHandler = (options: Filter[]): void =>
    handleMultiFilterChange(["global_params", "portfolio_index_id"], options);
  const scopeFilterChangeHandler = (option: Filter): void =>
    handleFilterChange(["global_params", "scope_id"], option);

  return (
    <BackgroundContainer>
      <Column flex={show ? "0 0 15%" : "0 0 0%"} show={show}>
        <SideMenu show={show} setShow={setShow} />
      </Column>
      <Column flex="1" bg="#F5F5F5" overflow={true}>
        <NavBarContainer>
          <RowContainer mt={1} mb={1}>
            <NavBar address={"control-panel"} />
          </RowContainer>
          <RowContainer ai={"flex-start"} jc={"flex-start"} mt={-15}>
            <CompassMainHeader pt={1} ml={12}>
              Control Panel
            </CompassMainHeader>
          </RowContainer>
          <RowContainer ai={"flex-start"} jc={"flex-start"} mt={1}>
            <CompassParagraph ml={20} color={greyText}>
              {MESSAGES.CONTROL_PANEL_DESCRIPTION}
            </CompassParagraph>
          </RowContainer>
          <RowContainer jc="flex-start" ai="center" ml={8}>
            <CustomDropdown
              options={fundOptions}
              placeholder={DEFAULT_FUND_FILTER}
              isMultiSelect={true}
              onSelectOptions={fundFilterChangeHandler}
              isLoading={isLoading}
            />
            <CustomDropdown
              options={SCOPE_FILTERS}
              placeholder={DEFAULT_SCOPE_FILTER}
              onSelectOption={scopeFilterChangeHandler}
              isLoading={isLoading}
            />
          </RowContainer>
        </NavBarContainer>
        <RowContainer>
          <PortfolioComposition
            controlPanelData={controlPanelData.security_types_ratios}
            isLoading={isLoading}
          />
        </RowContainer>
        <RowContainer jc="flex-start" ai="center">
          <Card h="500px" w={70} m="0 10px 0 20px" isLoading={isLoading}>
            <PortfolioEmissionsPie
              data={controlPanelData.ghg_emissions_by_portfolio}
            />
            <HorizontalLine marginTop={-10} color="#E2E8F0" />
            <ElementContainer mt={25}>
              <MiniChartContainer>
                <ImplicitTemperature params={controlPanelFilters} />
              </MiniChartContainer>
              <MiniChartContainer>
                <FossilFuelExposure params={controlPanelFilters} />
              </MiniChartContainer>
            </ElementContainer>
          </Card>
          <Card h="500px" m="0 20px 0 10px" isLoading={isLoading}>
            <DecarbonisationPathwayCard
              decarbonisationPathwayData={
                controlPanelData.carbon_pathway_by_portfolio
              }
            />
          </Card>
        </RowContainer>
        <RowContainer>
          <LatestNews
            portfolioIndexIds={
              controlPanelFilters.global_params.portfolio_index_id
            }
            height={BOTTOM_ROW_HEIGHT}
          />
          <ActionTracker height={BOTTOM_ROW_HEIGHT} />
          <TopContributors
            params={controlPanelFilters}
            height={BOTTOM_ROW_HEIGHT}
          />
        </RowContainer>
        <RowContainer />
      </Column>
    </BackgroundContainer>
  );
};
